import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { toast } from "sonner";
import { Search, Check, ChevronLeft, ChevronRight, Keyboard } from "lucide-react";
import { getDoctors, getAvailableSlots, createAppointment, updateDoctor, getPatients } from "../../api/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import Tutorial, { ctrlDStep, arrowsStep, enterStep, escStep } from "../../components/Tutorial";
import { cn } from "../../lib/utils";

const STEPS = ["Doctor & Slot", "Patient & Review"];

const OPD_TUTORIAL_ID = "opd-booking";
// Each step here has to actually be *done*, not just read — see
// Tutorial.jsx. Ctrl + D is tried right here on the OPD page itself, which
// is harmless: it's already the page that shortcut opens.
const OPD_TUTORIAL_STEPS = [
  ctrlDStep({
    title: "Jump in from anywhere",
    body: "Ctrl + D opens OPD Booking instantly from any screen in MediPro — no clicking through menus. Try it now.",
  }),
  arrowsStep({
    title: "Move around with arrow keys",
    body: "Use the arrow keys to move between doctors, dates and time slots — left, right, up, down, just like a grid. Tap all four.",
  }),
  enterStep({
    title: "Enter picks and continues",
    body: "Press Enter to open a doctor's slots, pick a time, or move on to the next step — one key instead of a click.",
  }),
  escStep({
    title: "Esc backs out",
    body: "Press Esc to close an expanded doctor card and step back — no need to reach for the mouse.",
  }),
];
// India-only, 6 digits, no leading zero — mirrors PINCODE_PATTERN in
// medipro-backend/validations/validations.js. Required so every new patient
// lands on the Stats page's location map instead of the "no usable pincode"
// bucket.
const PINCODE_PATTERN = /^[1-9]\d{5}$/;
// India-only mobile number: exactly 10 digits, starting 6-9 (the backend's
// own Joi rule only checks a minimum length, but a real Indian mobile is
// never shorter or longer than this, and WhatsApp delivery depends on it).
const PHONE_PATTERN = /^[6-9]\d{9}$/;
const VISIT_TYPES = [
  { key: "center", label: "Center Visit" },
  { key: "video", label: "Online" },
  { key: "home", label: "Home Visit" },
  { key: "walkin", label: "Walk In" },
];

// `offset` shifts the whole 10-day window forward/backward in whole weeks,
// so "Next" can reach arbitrarily far into the future for booking ahead.
const dateRange = (offset = 0) => {
  const today = moment();
  return Array.from({ length: 10 }, (_, i) => today.clone().add(offset + i - 3, "days"));
};

export default function OpdBooking() {
  const [step, setStep] = useState(1);
  // The furthest step reached so far — lets the step tabs act as back
  // navigation to anywhere already validated, without letting a click skip
  // ahead past a step that hasn't been filled in yet.
  const [maxStepReached, setMaxStepReached] = useState(1);
  const [doctors, setDoctors] = useState([]);
  const [doctorSearch, setDoctorSearch] = useState("");
  const [departmentId, setDepartmentId] = useState("");

  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [visitType, setVisitType] = useState("center");
  const [isoDate, setIsoDate] = useState(moment().format("YYYY-MM-DD"));
  const [dateOffset, setDateOffset] = useState(0);
  const [slotInfo, setSlotInfo] = useState({ onLeave: false, closed: false, slots: [] });
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [patient, setPatient] = useState({
    name: "",
    phone: "",
    email: "",
    gender: "",
    address: { line: "", city: "", state: "", pincode: "" },
  });
  const [pincodeLookupLoading, setPincodeLookupLoading] = useState(false);
  // Existing-patient autocomplete for the Name/Mobile fields — same search
  // (name OR phone) regardless of which field triggered it, so typing a
  // name suggests by name and typing digits suggests by phone. Tracked per
  // field only so each input's dropdown opens/closes independently.
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [phoneSuggestions, setPhoneSuggestions] = useState([]);
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);
  // null = no manual override yet, so it tracks the selected doctor's fee.
  // Reset whenever the doctor changes so a stale edited amount from a
  // previous doctor doesn't silently carry over.
  const [feeOverride, setFeeOverride] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Shown automatically the first time this account hasn't dismissed it yet
  // (seen_tutorials comes from the server on login — see Tutorial.jsx) — and
  // re-openable any time via the header's keyboard-shortcuts button.
  const user = useSelector((s) => s.users.user);
  const [tutorialOpen, setTutorialOpen] = useState(
    () => !(user?.seen_tutorials || []).includes(OPD_TUTORIAL_ID)
  );

  const loadDoctors = () =>
    getDoctors({ search: doctorSearch, limit: 50 })
      .then((res) => setDoctors(res.data))
      .catch((err) => toast.error(err?.message || "Could not load doctors."));

  useEffect(() => {
    loadDoctors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorSearch]);

  useEffect(() => {
    setMaxStepReached((m) => Math.max(m, step));
  }, [step]);

  useEffect(() => {
    if (!showNameSuggestions || patient.name.trim().length < 2) {
      setNameSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      getPatients({ search: patient.name.trim(), limit: 8 })
        .then((res) => setNameSuggestions(res.data || []))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [patient.name, showNameSuggestions]);

  useEffect(() => {
    if (!showPhoneSuggestions || patient.phone.trim().length < 3) {
      setPhoneSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      getPatients({ search: patient.phone.trim(), limit: 8 })
        .then((res) => setPhoneSuggestions(res.data || []))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [patient.phone, showPhoneSuggestions]);

  // Selecting a suggestion (from either field) fills in everything already
  // on file for that patient — not just name/phone — since the whole point
  // is avoiding retyping (and accidentally duplicating) an existing record.
  const applyExistingPatient = (p) => {
    setPatient({
      name: p.name || "",
      phone: p.phone || "",
      email: p.email || "",
      gender: p.gender || "",
      address: {
        line: p.address?.line || "",
        city: p.address?.city || "",
        state: p.address?.state || "",
        pincode: p.address?.pincode || "",
      },
    });
    setShowNameSuggestions(false);
    setShowPhoneSuggestions(false);
  };

  const isWalkIn = visitType === "walkin";

  // Set right when a doctor is freshly selected (see selectDoctor) and
  // consumed the next time slots load — so landing on the first open slot
  // only happens for that one moment, not every time isoDate changes too
  // (browsing days with the date strip's own arrow keys would otherwise
  // keep yanking focus back into the slot grid after each move).
  const autoFocusSlotRef = useRef(false);

  useEffect(() => {
    if (!selectedDoctor || step !== 1 || isWalkIn) return;
    getAvailableSlots({ doctor_id: selectedDoctor._id, iso_date: isoDate })
      .then((res) => {
        setSlotInfo(res.data);
        setSelectedSlot(null);
        if (autoFocusSlotRef.current) {
          autoFocusSlotRef.current = false;
          const slots = res.data?.slots || [];
          const firstOpen = slots.find((s) => !s.taken && !s.vip) || slots.find((s) => !s.taken);
          if (firstOpen) requestAnimationFrame(() => slotButtonRefs.current[firstOpen.time]?.focus());
        }
      })
      .catch((err) => toast.error(err?.message || "Could not load available slots."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoctor, isoDate, step, isWalkIn]);

  // A walk-in is, by definition, someone already at the clinic right now —
  // there's no future date to pick, so switching to this visit type snaps
  // straight back to today regardless of whatever date was browsed to
  // before (and blocks navigating away from it — see the date row below).
  useEffect(() => {
    if (!isWalkIn) return;
    setIsoDate(moment().format("YYYY-MM-DD"));
    setDateOffset(0);
  }, [isWalkIn]);

  useEffect(() => {
    const pincode = patient.address.pincode;
    if (!/^\d{6}$/.test(pincode)) return;

    let cancelled = false;
    setPincodeLookupLoading(true);
    fetch(`https://api.postalpincode.in/pincode/${pincode}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const postOffice = data?.[0]?.PostOffice?.[0];
        if (postOffice) {
          setPatient((p) => ({
            ...p,
            address: { ...p.address, city: postOffice.District, state: postOffice.State },
          }));
        } else {
          toast.error("Couldn't find that pincode — enter city/state manually.");
        }
      })
      .catch(() => toast.error("Couldn't look up pincode — enter city/state manually."))
      .finally(() => !cancelled && setPincodeLookupLoading(false));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.address.pincode]);

  const departments = useMemo(() => {
    const byId = new Map();
    doctors.forEach((d) => {
      if (d.department_id?._id) byId.set(d.department_id._id, d.department_id.name);
    });
    return [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [doctors]);
  const filteredDoctors = departmentId ? doctors.filter((d) => d.department_id?._id === departmentId) : doctors;

  // Refs for the whole keyboard-driven flow (Tally-style: search -> visit
  // type -> doctor grid -> slots, all reachable with arrow keys, no mouse
  // required). Each element's onKeyDown below owns just its own
  // transitions — simpler and safer than one global window listener trying
  // to guess what's currently focused.
  const searchInputRef = useRef(null);
  const visitTypeRefs = useRef({});
  const doctorCardRefs = useRef({});
  const dateButtonRefs = useRef({});
  const slotButtonRefs = useRef({});
  const bookAnotherRef = useRef(null);
  const goToAppointmentsRef = useRef(null);

  // Arrow keys only do anything once *something* on the page has focus —
  // on a fresh page load nothing does, so the very first keypress would go
  // nowhere. Auto-focusing the search box the moment this step is shown
  // (first load, or coming Back from Patient) means arrows work
  // immediately, no click or Tab needed first.
  useEffect(() => {
    if (step === 1) searchInputRef.current?.focus();
  }, [step]);

  // Same reasoning on the confirmation screen — land on its primary action
  // so Left/Right between the two buttons (see below) works right away.
  useEffect(() => {
    if (success) goToAppointmentsRef.current?.focus();
  }, [success]);

  // Same reasoning for step 2 — land on Patient with Name already focused
  // so the arrow-key/Enter flow below works right away.
  useEffect(() => {
    if (step === 2) patientFieldRefs.current.name?.focus();
  }, [step]);

  const focusVisitType = () => visitTypeRefs.current[visitType]?.focus();

  const focusDoctorCard = (doctor) => {
    const el = doctorCardRefs.current[doctor._id];
    el?.focus();
    el?.scrollIntoView({ block: "nearest" });
  };

  // Closing the tutorial should hand focus straight to the first doctor
  // card — not select/expand it, just position keyboard focus there — so
  // the arrow keys just taught actually have something to move between
  // right away. If the doctor list hasn't finished loading yet, the ref
  // below defers this until it has.
  const focusFirstDoctorOnLoadRef = useRef(false);
  const closeTutorial = () => {
    setTutorialOpen(false);
    if (filteredDoctors[0]) requestAnimationFrame(() => focusDoctorCard(filteredDoctors[0]));
    else focusFirstDoctorOnLoadRef.current = true;
  };
  useEffect(() => {
    if (focusFirstDoctorOnLoadRef.current && filteredDoctors[0]) {
      focusFirstDoctorOnLoadRef.current = false;
      focusDoctorCard(filteredDoctors[0]);
    }
  }, [filteredDoctors]);

  // Selecting a doctor — by click or by keyboard — both picks it AND
  // expands its card in place to show slots right there (see the render
  // below), instead of requiring a separate "Next" to a whole new screen.
  const selectDoctor = (doctor) => {
    setSelectedDoctor(doctor);
    setFeeOverride(null);
    setDateOffset(0);
    autoFocusSlotRef.current = true;
  };

  // Esc from anywhere inside an expanded card (its header, the date strip,
  // or a slot) collapses it back to a plain compact card and returns focus
  // to its header — same "close and hand focus back to what opened it"
  // pattern as dismissing a modal or popover.
  const closeDoctorCard = (doctor) => {
    setSelectedDoctor(null);
    setSelectedSlot(null);
    focusDoctorCard(doctor);
  };

  // Deep link from the Appointments list — clicking a doctor's name there
  // lands here with `?doctor=<id>` and should open straight to that
  // doctor's slots, same as picking them by hand, instead of making the
  // user search for them again. Guarded to fire once so re-fetching the
  // doctor list (e.g. while typing a search) doesn't keep re-selecting.
  const appliedInitialDoctorRef = useRef(false);
  useEffect(() => {
    if (appliedInitialDoctorRef.current) return;
    const doctorId = searchParams.get("doctor");
    if (!doctorId || doctors.length === 0) return;
    const doctor = doctors.find((d) => d._id === doctorId);
    if (!doctor) return;
    appliedInitialDoctorRef.current = true;
    selectDoctor(doctor);
    requestAnimationFrame(() => focusDoctorCard(doctor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctors, searchParams]);

  // Generic row-aware vertical neighbor finder, shared by the doctor grid
  // and the slot grid below: groups items by their *actual rendered row*
  // (getBoundingClientRect, not index arithmetic — column count varies with
  // container width, and a row-spanning expanded card shifts everything
  // after it down a row) and returns whichever item in the row above/below
  // sits horizontally closest to the current one. `undefined` means there's
  // no row in that direction — the caller decides what "exiting the grid"
  // means; `null` means the current item isn't currently rendered.
  const findRowNeighbor = (direction, items, refsMap, currentId, idOf) => {
    const rows = new Map();
    for (const item of items) {
      const el = refsMap.current[idOf(item)];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const top = Math.round(rect.top);
      if (!rows.has(top)) rows.set(top, []);
      rows.get(top).push({ item, rect });
    }
    const rowTops = [...rows.keys()].sort((a, b) => a - b);
    const currentEl = refsMap.current[currentId];
    if (!currentEl) return null;
    const currentRect = currentEl.getBoundingClientRect();
    const currentRowIdx = rowTops.indexOf(Math.round(currentRect.top));
    const targetRowIdx = direction === "down" ? currentRowIdx + 1 : currentRowIdx - 1;
    if (targetRowIdx < 0 || targetRowIdx >= rowTops.length) return undefined;
    const targetRow = rows.get(rowTops[targetRowIdx]);
    const currentCenterX = currentRect.left + currentRect.width / 2;
    return targetRow.reduce((best, entry) => {
      const dist = Math.abs(entry.rect.left + entry.rect.width / 2 - currentCenterX);
      return dist < best.dist ? { item: entry.item, dist } : best;
    }, { item: targetRow[0].item, dist: Infinity }).item;
  };

  const findVerticalNeighbor = (direction, currentId) =>
    findRowNeighbor(direction, filteredDoctors, doctorCardRefs, currentId, (d) => d._id);

  const findSlotVerticalNeighbor = (direction, currentTime) =>
    findRowNeighbor(direction, slotInfo.slots.filter((s) => !s.taken), slotButtonRefs, currentTime, (s) => s.time);

  // Patient step — same row-aware arrow-key movement as the doctor/slot
  // grids above, plus Enter to move on (or confirm, from the last field).
  // Left/Right only jump fields at the very start/end of the current text —
  // anywhere else they still move the caret like a normal text input would.
  const PATIENT_FIELD_ORDER = ["name", "phone", "email", "gender", "address-line", "pincode", "city", "state"];
  const patientFieldRefs = useRef({});
  const focusPatientField = (id) => patientFieldRefs.current[id]?.focus();
  const handlePatientFieldKeyDown = (e, id, { suggestionsOpen = false } = {}) => {
    const el = e.currentTarget;
    if ((e.key === "ArrowDown" || e.key === "ArrowUp") && !suggestionsOpen) {
      e.preventDefault();
      const neighbor = findRowNeighbor(
        e.key === "ArrowDown" ? "down" : "up",
        PATIENT_FIELD_ORDER,
        patientFieldRefs,
        id,
        (fieldId) => fieldId
      );
      if (neighbor) focusPatientField(neighbor);
      return;
    }
    // A <select> already uses Left/Right natively to change its value —
    // don't fight that by also treating them as field-to-field navigation.
    if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && el.tagName !== "SELECT") {
      const atStart = el.selectionStart === 0 && el.selectionEnd === 0;
      const atEnd = el.selectionStart === el.value.length && el.selectionEnd === el.value.length;
      if ((e.key === "ArrowRight" && !atEnd) || (e.key === "ArrowLeft" && !atStart)) return;
      e.preventDefault();
      const nextId = PATIENT_FIELD_ORDER[PATIENT_FIELD_ORDER.indexOf(id) + (e.key === "ArrowRight" ? 1 : -1)];
      if (nextId) focusPatientField(nextId);
      return;
    }
    // A native <select> doesn't actually open on Enter by itself (only
    // Space/click do, depending on the browser) — showPicker() opens it for
    // real. Once it already has a value, a second Enter just moves on
    // instead, same as every other field.
    if (e.key === "Enter" && el.tagName === "SELECT" && !el.value) {
      e.preventDefault();
      el.showPicker?.();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const nextId = PATIENT_FIELD_ORDER[PATIENT_FIELD_ORDER.indexOf(id) + 1];
      if (nextId) focusPatientField(nextId);
      else if (canProceed()) handleNext();
    }
  };

  // Lands on the first regular slot, so keyboard booking doesn't drop
  // someone into a VIP slot just by pressing Down then Enter.
  const firstOpenSlotTime = () =>
    (slotInfo.slots.find((s) => !s.taken && !s.vip) || slotInfo.slots.find((s) => !s.taken))?.time;

  // Left/Right through the date strip — moves the actual selected date by
  // a day (not just focus, like the doctor/slot grids: there's only ever
  // one date "selected" at a time, so highlighting one without the other
  // wouldn't mean anything). Paging the visible 10-day window when the new
  // date would fall outside it mirrors the </> buttons' own ±7-day jump, so
  // arrowing past the edge continues smoothly instead of hitting a wall.
  const moveDate = (deltaDays) => {
    const next = moment(isoDate, "YYYY-MM-DD").add(deltaDays, "day");
    if (next.isBefore(moment(), "day")) return;
    const nextIso = next.format("YYYY-MM-DD");
    setIsoDate(nextIso);
    const windowStart = moment().add(dateOffset - 3, "day");
    const windowEnd = moment().add(dateOffset + 6, "day");
    if (next.isBefore(windowStart, "day")) setDateOffset((o) => Math.max(0, o - 7));
    else if (next.isAfter(windowEnd, "day")) setDateOffset((o) => o + 7);
    requestAnimationFrame(() => {
      dateButtonRefs.current[nextIso]?.focus();
      dateButtonRefs.current[nextIso]?.scrollIntoView({ block: "nearest", inline: "center" });
    });
  };

  const handleDateKeyDown = (e, d) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeDoctorCard(d);
      return;
    }
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      moveDate(e.key === "ArrowRight" ? 1 : -1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusDoctorCard(d);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const firstSlot = firstOpenSlotTime();
      if (firstSlot) slotButtonRefs.current[firstSlot]?.focus();
    }
  };

  // Arrow keys only ever move focus around the grid — they never expand a
  // card or fetch its slots, so browsing past several doctors on the way to
  // the one you want doesn't fire a slot lookup for each one in passing.
  // Only Enter or an actual click commits a selection (see selectDoctor).
  const handleDoctorCardKeyDown = (e, d) => {
    if (e.key === "Enter") {
      e.preventDefault();
      selectDoctor(d);
      // A walk-in has no slots to pick at all — there's nothing left to do
      // on this step once a doctor's chosen, so Enter goes straight on to
      // Patient instead of just expanding a card with nothing in it.
      if (isWalkIn) handleNext();
      return;
    }
    if (e.key === "Escape" && selectedDoctor?._id === d._id) {
      e.preventDefault();
      closeDoctorCard(d);
      return;
    }
    if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)) return;

    // Down from the *expanded* (already-selected) card's header moves to
    // the date strip next, rather than to the next grid row.
    if (e.key === "ArrowDown" && selectedDoctor?._id === d._id && !isWalkIn) {
      e.preventDefault();
      dateButtonRefs.current[isoDate]?.focus();
      return;
    }

    e.preventDefault();

    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      const currentIndex = filteredDoctors.findIndex((doc) => doc._id === d._id);
      const nextIndex = Math.min(
        Math.max(currentIndex + (e.key === "ArrowRight" ? 1 : -1), 0),
        filteredDoctors.length - 1
      );
      focusDoctorCard(filteredDoctors[nextIndex]);
      return;
    }

    const neighbor = findVerticalNeighbor(e.key === "ArrowDown" ? "down" : "up", d._id);
    if (neighbor === undefined) {
      // Top row + Up (or an empty grid) exits back up to Search, which
      // sits directly above the grid — Down past the last row just stays
      // put, there's nothing below it.
      if (e.key === "ArrowUp") searchInputRef.current?.focus();
      return;
    }
    if (neighbor) focusDoctorCard(neighbor);
  };

  // Left/Right move between the *enabled* slots only — a taken slot can't
  // receive focus at all (disabled buttons are unfocusable), so skipping
  // over them here keeps the arrow key from silently doing nothing on one.
  // Up exits back to the doctor's own header button, keeping the keyboard
  // flow from ever dead-ending once it reaches the slot row. Position is
  // tracked from the slot the event actually fired on, not from
  // `selectedSlot` — the first slot gets focused automatically but isn't
  // marked selected until confirmed, so keying off selection state would
  // make the very first Right press look like a no-op.
  const selectSlot = (s) => {
    setSelectedSlot(s.time);
    slotButtonRefs.current[s.time]?.focus();
  };

  const handleSlotKeyDown = (e, slot, doctor) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeDoctorCard(doctor);
      return;
    }
    // Enter both picks this slot and moves straight on to the Patient step
    // — no separate click on Next needed once a time's been chosen.
    if (e.key === "Enter") {
      e.preventDefault();
      setSelectedSlot(slot.time);
      handleNext();
      return;
    }
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const direction = e.key === "ArrowDown" ? "down" : "up";
      const neighbor = findSlotVerticalNeighbor(direction, slot.time);
      if (neighbor) {
        selectSlot(neighbor);
        return;
      }
      // Top row + Up exits back to the date strip (the thing directly
      // above the slots). Bottom row + Down exits the slot grid entirely,
      // to the next doctor card below — "coming out the bottom" of one
      // doctor's slots continues on to the next one instead of getting stuck.
      if (direction === "up") {
        dateButtonRefs.current[isoDate]?.focus();
      } else {
        const currentDoctorIndex = filteredDoctors.findIndex((d) => d._id === doctor._id);
        const next = filteredDoctors[currentDoctorIndex + 1];
        if (next) focusDoctorCard(next);
      }
      return;
    }
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const openSlots = slotInfo.slots.filter((s) => !s.taken);
    const currentIndex = openSlots.findIndex((s) => s.time === slot.time);
    const delta = e.key === "ArrowRight" ? 1 : -1;
    const nextIndex = Math.min(Math.max(currentIndex + delta, 0), openSlots.length - 1);
    const next = openSlots[nextIndex];
    if (next) selectSlot(next);
  };

  // Doctor & Slot is one merged step, and Patient & Review is the other — a
  // walk-in just never needs `selectedSlot` since it has no real slot to book.
  const canProceed = () => {
    if (step === 1) return !!selectedDoctor && (isWalkIn || !!selectedSlot);
    if (step === 2) return patient.name && PHONE_PATTERN.test(patient.phone) && PINCODE_PATTERN.test(patient.address.pincode);
    return true;
  };

  const handleNext = () => {
    if (step === 2) return handleConfirm();
    setStep((s) => Math.min(s + 1, 2));
  };

  const handleBack = () => setStep((s) => s - 1);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const effectiveFee = Number(feeOverride ?? selectedDoctor.fee);
      // Editing the fee here changes the doctor's standard rate for every
      // future booking, not just this one — past appointments already keep
      // their own snapshot fee regardless, so nothing retroactive happens.
      if (feeOverride !== null && effectiveFee !== selectedDoctor.fee) {
        await updateDoctor(selectedDoctor._id, { fee: effectiveFee });
        loadDoctors(); // so the doctor list reflects the new fee without a page reload
      }
      const res = await createAppointment({
        doctor_id: selectedDoctor._id,
        iso_date: isoDate,
        slot_time: selectedSlot,
        visit_type: visitType,
        fee: effectiveFee,
        patient: {
          name: patient.name,
          phone: patient.phone,
          email: patient.email || undefined,
          gender: patient.gender || undefined,
          address: patient.address,
        },
      });
      setSuccess(res.data);
      toast.success("Appointment confirmed and patient notified via WhatsApp.");
    } catch (err) {
      toast.error(err?.message || "Could not confirm booking.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetWizard = () => {
    loadDoctors();
    setStep(1);
    setMaxStepReached(1);
    setSelectedDoctor(null);
    setFeeOverride(null);
    setVisitType("center");
    setIsoDate(moment().format("YYYY-MM-DD"));
    setDateOffset(0);
    setSlotInfo({ onLeave: false, closed: false, slots: [] });
    setSelectedSlot(null);
    setPatient({ name: "", phone: "", email: "", gender: "", address: { line: "", city: "", state: "", pincode: "" } });
    setSuccess(null);
  };

  if (success) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-secondary/10 text-secondary">
          <Check className="h-7 w-7" />
        </span>
        <h2 className="font-serif text-2xl font-semibold">Booking confirmed</h2>
        <p className="mt-1 text-sm text-muted-foreground">Appointment #{success._id.slice(-6)}</p>
        <div className="mt-6 flex justify-center gap-3">
          <Button
            ref={bookAnotherRef}
            variant="outline"
            onClick={resetWizard}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") {
                e.preventDefault();
                goToAppointmentsRef.current?.focus();
              }
            }}
          >
            Book another
          </Button>
          <Button
            ref={goToAppointmentsRef}
            onClick={() => navigate("/appointments")}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") {
                e.preventDefault();
                bookAnotherRef.current?.focus();
              }
            }}
          >
            Go to appointments
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-full max-w-6xl flex-col">
      <Tutorial
        id={OPD_TUTORIAL_ID}
        eyebrow="Booking is keyboard-first"
        steps={OPD_TUTORIAL_STEPS}
        open={tutorialOpen}
        onOpenChange={(next) => (next ? setTutorialOpen(true) : closeTutorial())}
      />
      <div className="flex-1 space-y-3 overflow-y-auto pb-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl font-semibold">New OPD booking</h2>
          <p className="text-xs text-muted-foreground">Book an out-patient consultation in three quick steps</p>
        </div>
        <button
          type="button"
          onClick={() => setTutorialOpen(true)}
          title="Keyboard shortcuts"
          className="flex shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <Keyboard className="h-3.5 w-3.5" /> Shortcuts
        </button>
      </div>

      <div>
        <div className="mb-2 flex justify-between text-xs font-medium text-muted-foreground">
          {STEPS.map((label, i) => {
            const stepNum = i + 1;
            const clickable = stepNum <= maxStepReached;
            return (
              <button
                key={label}
                type="button"
                disabled={!clickable}
                onClick={() => clickable && setStep(stepNum)}
                aria-current={stepNum === step ? "step" : undefined}
                className={cn(
                  "rounded px-0.5 transition-colors",
                  stepNum <= step ? "text-primary" : "text-muted-foreground",
                  clickable && stepNum !== step && "cursor-pointer hover:underline",
                  !clickable && "cursor-default"
                )}
              >
                {stepNum}. {label}
              </button>
            );
          })}
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-primary transition-all" style={{ width: `${(step / 2) * 100}%` }} />
        </div>
      </div>

      {step === 1 && (
        <div className="space-y-3">
          <div className="flex gap-1.5">
            {VISIT_TYPES.map((v, i) => (
              <button
                key={v.key}
                ref={(el) => {
                  visitTypeRefs.current[v.key] = el;
                }}
                onClick={() => setVisitType(v.key)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                    e.preventDefault();
                    const nextIdx = Math.min(Math.max(i + (e.key === "ArrowRight" ? 1 : -1), 0), VISIT_TYPES.length - 1);
                    const next = VISIT_TYPES[nextIdx];
                    setVisitType(next.key);
                    visitTypeRefs.current[next.key]?.focus();
                  } else if (e.key === "ArrowDown") {
                    // Visit Type sits above Search in the layout, so Down
                    // goes there next — not straight to the doctor grid.
                    e.preventDefault();
                    searchInputRef.current?.focus();
                  }
                }}
                className={cn(
                  "rounded-full border px-3 py-1 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  visitType === v.key ? "border-primary bg-primary text-primary-foreground" : "border-border"
                )}
              >
                {v.label}
              </button>
            ))}
          </div>
          {isWalkIn && (
            <p className="text-sm text-muted-foreground">
              Walk-ins skip slot scheduling entirely — pick a doctor and Next opens the patient form directly. The
              exact time they're actually seen is adjusted manually later, whenever that happens.
            </p>
          )}

          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Search doctor by name or speciality…"
                className="h-9 pl-9"
                value={doctorSearch}
                onChange={(e) => setDoctorSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    focusVisitType();
                  } else if (e.key === "ArrowDown") {
                    e.preventDefault();
                    const target = selectedDoctor || filteredDoctors[0];
                    if (target) focusDoctorCard(target);
                  }
                }}
              />
            </div>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
            >
              <option value="">All departments</option>
              {departments.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>

          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {filteredDoctors.map((d) => {
              const expanded = selectedDoctor?._id === d._id;
              return (
                <div
                  key={d._id}
                  className={cn(
                    "rounded-lg border transition",
                    expanded ? "border-primary bg-primary/5 sm:col-span-3 lg:col-span-4" : "border-border"
                  )}
                >
                  <button
                    ref={(el) => {
                      doctorCardRefs.current[d._id] = el;
                    }}
                    onClick={() => selectDoctor(d)}
                    onKeyDown={(e) => handleDoctorCardKeyDown(e, d)}
                    className={cn(
                      "flex w-full items-start justify-between p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      // The focus ring traces this button's own corners, not
                      // its parent's — without a matching radius here it
                      // shows up as a sharp rectangle poking out of the
                      // rounded card around it.
                      expanded ? "rounded-t-lg" : "rounded-lg"
                    )}
                  >
                    <div>
                      <p className="font-medium">{d.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {[d.department_id?.name, d.specialty].filter(Boolean).join(" · ")}
                      </p>
                      {d.on_leave_today && <Badge variant="destructive" className="mt-1">On leave</Badge>}
                      <p className="mt-1 text-xs text-muted-foreground">{d.gender} · Reg: {d.reg_no}</p>
                    </div>
                    <div className="text-right text-sm font-medium">₹{d.fee}</div>
                  </button>

                  {/* Expands in place instead of a separate "Slot" screen —
                      picking a doctor (click or Enter) reveals its slots
                      right here, so getting from "pick a doctor" to "pick a
                      time" never needs Next. */}
                  {expanded && !isWalkIn && (
                    <div className="space-y-3 border-t border-border p-3">
                      {(() => {
                        const visible = dateRange(dateOffset);
                        const first = visible[0];
                        const last = visible[visible.length - 1];
                        const sameMonth = first.isSame(last, "month");
                        return (
                          <p className="text-sm font-medium text-muted-foreground">
                            {sameMonth ? first.format("MMMM YYYY") : `${first.format("MMM")} – ${last.format("MMM YYYY")}`}
                          </p>
                        );
                      })()}

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setDateOffset((o) => Math.max(0, o - 7))}
                          disabled={dateOffset <= 0}
                          aria-label="Earlier dates"
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border disabled:opacity-30"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>

                        <div className="flex min-w-0 flex-1 flex-wrap gap-2 sm:flex-nowrap sm:overflow-x-auto sm:pb-1">
                          {dateRange(dateOffset).map((dt) => {
                            const iso = dt.format("YYYY-MM-DD");
                            const isPast = dt.isBefore(moment(), "day");
                            const active = iso === isoDate;
                            return (
                              <button
                                key={iso}
                                ref={(el) => {
                                  dateButtonRefs.current[iso] = el;
                                }}
                                disabled={isPast}
                                onClick={() => setIsoDate(iso)}
                                onKeyDown={(e) => handleDateKeyDown(e, d)}
                                className={cn(
                                  "flex w-14 shrink-0 flex-col items-center rounded-lg border py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                  isPast && "opacity-40",
                                  active ? "border-primary bg-primary/5" : "border-border"
                                )}
                              >
                                <span className="text-xs text-muted-foreground">{dt.format("ddd")}</span>
                                <span className="font-medium">{dt.format("D")}</span>
                              </button>
                            );
                          })}
                        </div>

                        <button
                          type="button"
                          onClick={() => setDateOffset((o) => o + 7)}
                          aria-label="Later dates — book further ahead"
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>

                      {slotInfo.closed ? (
                        <p className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                          The clinic is closed this day. Please pick another date.
                        </p>
                      ) : slotInfo.onLeave ? (
                        <p className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                          {d.name} is on leave this day. Please pick another date.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {slotInfo.slots.map((s) => (
                            <button
                              key={s.time}
                              ref={(el) => {
                                slotButtonRefs.current[s.time] = el;
                              }}
                              disabled={s.taken}
                              onClick={() => setSelectedSlot(s.time)}
                              onKeyDown={(e) => handleSlotKeyDown(e, s, d)}
                              className={cn(
                                "rounded-md border px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                s.taken && "cursor-not-allowed opacity-40 line-through",
                                selectedSlot === s.time
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : s.vip
                                    ? "border-warning/50 bg-warning/10 text-warning"
                                    : "border-border"
                              )}
                            >
                              {s.vip && <span className="font-semibold">VIP Slot · </span>}
                              {s.time}
                            </button>
                          ))}
                        </div>
                      )}

                      <div className="rounded-md bg-muted p-2 text-sm">
                        Consult fee: <b>₹{d.fee}</b>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="mx-auto grid max-w-5xl gap-4 lg:grid-cols-3">
        <div className="grid gap-4 sm:grid-cols-2 lg:col-span-2">
          <div className="relative space-y-1.5 sm:col-span-2">
            <label className="text-sm font-medium">Name *</label>
            <Input
              ref={(el) => {
                patientFieldRefs.current.name = el;
              }}
              autoComplete="off"
              value={patient.name}
              onChange={(e) => {
                setPatient((p) => ({ ...p, name: e.target.value }));
                setShowNameSuggestions(true);
              }}
              onFocus={() => setShowNameSuggestions(true)}
              onBlur={() => setTimeout(() => setShowNameSuggestions(false), 150)}
              onKeyDown={(e) =>
                handlePatientFieldKeyDown(e, "name", { suggestionsOpen: showNameSuggestions && nameSuggestions.length > 0 })
              }
            />
            {showNameSuggestions && nameSuggestions.length > 0 && (
              <div className="absolute z-10 max-h-56 w-full overflow-auto rounded-md border border-border bg-background shadow-md">
                {nameSuggestions.map((p) => (
                  <button
                    key={p._id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyExistingPatient(p)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    <span className="font-medium">{p.name}</span>
                    <span className="text-muted-foreground">{p.phone}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative space-y-1.5">
            <label className="text-sm font-medium">Mobile *</label>
            <Input
              ref={(el) => {
                patientFieldRefs.current.phone = el;
              }}
              autoComplete="off"
              maxLength={10}
              placeholder="10-digit mobile number"
              value={patient.phone}
              onChange={(e) => {
                setPatient((p) => ({ ...p, phone: e.target.value.replace(/\D/g, "") }));
                setShowPhoneSuggestions(true);
              }}
              onFocus={() => setShowPhoneSuggestions(true)}
              onBlur={() => setTimeout(() => setShowPhoneSuggestions(false), 150)}
              onKeyDown={(e) =>
                handlePatientFieldKeyDown(e, "phone", { suggestionsOpen: showPhoneSuggestions && phoneSuggestions.length > 0 })
              }
            />
            {patient.phone.length > 0 && !PHONE_PATTERN.test(patient.phone) && (
              <p className="text-xs text-destructive">Enter a valid 10-digit mobile number</p>
            )}
            {showPhoneSuggestions && phoneSuggestions.length > 0 && (
              <div className="absolute z-10 max-h-56 w-full overflow-auto rounded-md border border-border bg-background shadow-md">
                {phoneSuggestions.map((p) => (
                  <button
                    key={p._id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyExistingPatient(p)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    <span className="font-medium">{p.name}</span>
                    <span className="text-muted-foreground">{p.phone}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Email</label>
            <Input
              ref={(el) => {
                patientFieldRefs.current.email = el;
              }}
              type="email"
              value={patient.email}
              onChange={(e) => setPatient((p) => ({ ...p, email: e.target.value }))}
              onKeyDown={(e) => handlePatientFieldKeyDown(e, "email")}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Gender</label>
            <select
              ref={(el) => {
                patientFieldRefs.current.gender = el;
              }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={patient.gender}
              onChange={(e) => setPatient((p) => ({ ...p, gender: e.target.value }))}
              onKeyDown={(e) => handlePatientFieldKeyDown(e, "gender")}
            >
              <option value="">Select</option>
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
            </select>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <label className="text-sm font-medium">Address</label>
            <Input
              ref={(el) => {
                patientFieldRefs.current["address-line"] = el;
              }}
              placeholder="House no., street, area"
              value={patient.address.line}
              onChange={(e) => setPatient((p) => ({ ...p, address: { ...p.address, line: e.target.value } }))}
              onKeyDown={(e) => handlePatientFieldKeyDown(e, "address-line")}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Pincode *</label>
            <Input
              ref={(el) => {
                patientFieldRefs.current.pincode = el;
              }}
              maxLength={6}
              placeholder="e.g. 781001"
              value={patient.address.pincode}
              onChange={(e) =>
                setPatient((p) => ({ ...p, address: { ...p.address, pincode: e.target.value.replace(/\D/g, "") } }))
              }
              onKeyDown={(e) => handlePatientFieldKeyDown(e, "pincode")}
            />
            {pincodeLookupLoading && <p className="text-xs text-muted-foreground">Looking up city/state…</p>}
            {patient.address.pincode.length > 0 && !PINCODE_PATTERN.test(patient.address.pincode) && (
              <p className="text-xs text-destructive">Enter a valid 6-digit pincode</p>
            )}
          </div>
          <div />
          <div className="space-y-1.5">
            <label className="text-sm font-medium">City</label>
            <Input
              ref={(el) => {
                patientFieldRefs.current.city = el;
              }}
              value={patient.address.city}
              onChange={(e) => setPatient((p) => ({ ...p, address: { ...p.address, city: e.target.value } }))}
              onKeyDown={(e) => handlePatientFieldKeyDown(e, "city")}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">State</label>
            <Input
              ref={(el) => {
                patientFieldRefs.current.state = el;
              }}
              value={patient.address.state}
              onChange={(e) => setPatient((p) => ({ ...p, address: { ...p.address, state: e.target.value } }))}
              onKeyDown={(e) => handlePatientFieldKeyDown(e, "state")}
            />
          </div>
        </div>

        <div className="h-fit space-y-1 rounded-lg border border-border p-4 lg:col-span-1">
          <p className="pb-1 text-sm font-semibold">Booking summary</p>
          {[
            ["Doctor", selectedDoctor?.name],
            ["Department", selectedDoctor?.department_id?.name],
            ["Speciality", selectedDoctor?.specialty],
            ["Visit type", VISIT_TYPES.find((v) => v.key === visitType)?.label],
            [
              "Date & time",
              `${moment(isoDate).format("D MMM YYYY")}${
                selectedSlot ? `, ${selectedSlot}` : isWalkIn ? " — time adjusted manually later" : ""
              }`,
            ],
            ["Patient name", patient.name],
            ["Mobile number", patient.phone],
            ...(patient.address.city || patient.address.state
              ? [["Address", [patient.address.line, patient.address.city, patient.address.state, patient.address.pincode].filter(Boolean).join(", ")]]
              : []),
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between border-b border-border py-2 text-sm last:border-0">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-medium">{value}</span>
            </div>
          ))}
          <div className="flex items-center justify-between pt-3 text-base font-semibold">
            <span>Total payable</span>
            <span className="flex items-center gap-1">
              ₹
              <Input
                type="number"
                min={0}
                value={feeOverride ?? selectedDoctor?.fee ?? ""}
                onChange={(e) => setFeeOverride(e.target.value)}
                className="h-8 w-24 text-right text-base font-semibold"
              />
            </span>
          </div>
          {Number(feeOverride) !== selectedDoctor?.fee && feeOverride !== null && (
            <p className="text-right text-xs text-muted-foreground">
              Current fee for {selectedDoctor?.name} is ₹{selectedDoctor?.fee} — confirming will update it to ₹
              {feeOverride} for all future bookings. Past appointments keep their original fee.
            </p>
          )}
        </div>
        </div>
      )}

      </div>

      <div className="flex justify-between border-t border-border pt-4">
        <Button variant="outline" disabled={step === 1} onClick={handleBack}>
          Back
        </Button>
        <Button disabled={!canProceed() || submitting} onClick={handleNext}>
          {step === 2 ? (submitting ? "Confirming…" : "Confirm booking") : "Next"}
        </Button>
      </div>
    </div>
  );
}
